import styled, { css } from 'styled-components';

/**
 * Shared, app-wide modal/dialog scaffold, aligned with the design system
 * (see docs/design-system.md → "Modals & Dialogs"):
 *
 * - An elevated `--surface` card.
 * - A left-aligned, Title Case `DialogTitle` (never centered / uppercase /
 *   letter-spaced), with an optional muted `DialogSubtitle`.
 * - A right-aligned `DialogFooter` using the Primary / Secondary / Destructive
 *   button hierarchy. Green (`--primary`) is for the primary action only;
 *   Cancel/secondary actions are neutral.
 *
 * The common `Modal` supplies the top-right close button — dialogs never add
 * their own. This replaces the legacy per-file uppercase/green/centered modal
 * styling that used to be duplicated across the app.
 */

// Dialog width scale. `sm` is the default for simple confirm/rename dialogs;
// `lg` suits content-heavy dialogs (e.g. a folder tree to pick from); `xl` is
// for a dialog whose rows are a whole layout of their own — a frame, a title,
// what is said and who says it (the Add Clips picker), which at `lg` runs its
// right-hand column off the card.
const DIALOG_WIDTHS = {
  sm: '440px',
  md: '540px',
  lg: '580px',
  xl: '650px',
} as const;

export const DialogCard = styled.div<{ $size?: keyof typeof DIALOG_WIDTHS }>`
  width: ${({ $size = 'sm' }) => DIALOG_WIDTHS[$size]};
  max-width: calc(100vw - 2 * var(--spacing-lg));
  box-sizing: border-box;
  padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-lg);
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
  background: var(--surface);
  box-shadow: var(--elevation-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

// Groups a title with an optional subtitle as one header unit (tight gap),
// kept distinct from the dialog's larger inter-section gap.
export const DialogHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

export const DialogTitle = styled.h2`
  margin: 0;
  /* leave room for the modal's top-right close button */
  padding-right: var(--spacing-2xl);
  font-family: var(--font-primary);
  font-size: var(--text-md);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

// Secondary line under a DialogTitle — e.g. the subject being acted on. Stays
// single-line and truncates; pair with a `title` attribute for the full value.
export const DialogSubtitle = styled.p`
  margin: 0;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DialogText = styled.p`
  margin: 0;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--text-secondary);
`;

export const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
`;

const dialogButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  height: var(--btn-h-lg);
  padding: 0 var(--btn-px-lg);
  border-radius: var(--radius-button);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    filter 120ms ease,
    opacity 120ms ease;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const SecondaryButton = styled.button`
  ${dialogButtonBase}
  background: transparent;
  color: var(--foreground);
  border: 1px solid var(--border);
  font-weight: var(--font-weight-medium);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;

export const PrimaryButton = styled.button`
  ${dialogButtonBase}
  background: var(--primary);
  color: var(--background);
  border: none;
  font-weight: var(--font-weight-semi-bold);

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }
`;

export const DestructiveButton = styled.button`
  ${dialogButtonBase}
  background: var(--destructive);
  color: var(--destructive-foreground);
  border: none;
  font-weight: var(--font-weight-semi-bold);

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }
`;
